import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Link, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { venueGetSubscriptionSessionApi } from '../../../api/stripe/venueGetSubscriptionSessionApi';
import { useChatContext } from '../../../contexts/ChatContext/useChatContext';
import { useTermsAndConditions } from '../../../hooks/useTermsAndConditions';
import { useAppSelector } from '../../../redux/hooks';
import { StripePriceWithMetadata } from '../../../redux/features/subscription/types';
import Button from '../../Button/Button';
import SvgIcon from '../../Icons';
import BaseDialog from '../Base';
import { TermsAndConditionDialog } from '../TermsAndCondition/TermsAndConditionDialog';
import { MigrationRenewalState, STAY_ON_FREE_STORAGE_PREFIX } from './useMigrationRenewal';

type Props = {
  open: boolean;
  onClose: () => void;
  state: MigrationRenewalState;
};

const findRenewalPrice = (
  lastSku: string,
  isBrand: boolean,
  prices: StripePriceWithMetadata[],
): StripePriceWithMetadata | undefined => {
  // Brands have legacy and v3 (new) pricing tiers — match whichever the user
  // was on so they renew at the same package and price.
  const wasV3 = lastSku.replace(/_+$/, '').endsWith('_v3');
  const baseSku = lastSku
    .replace(/_+$/, '')
    .replace(/_final$/, '')
    .replace(/_v3$/, '');

  return prices.find((p) => {
    const productSku = p?.metadata?.ProductSku;
    if (!productSku?.startsWith(baseSku)) return false;
    if (isBrand) {
      return wasV3 ? productSku.endsWith('_v3') : !productSku.endsWith('_v3');
    }
    return true;
  });
};

const MigrationRenewalDialog: React.FC<Props> = ({ open, onClose, state }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const chatContext = useChatContext();
  const termsData = useTermsAndConditions();
  const { pricing } = useAppSelector((s) => s.subscription.value);
  const venueId = useAppSelector((s) => s.venue.value?.id);

  const [showTerms, setShowTerms] = React.useState(false);
  const [showStayFreeConfirm, setShowStayFreeConfirm] = React.useState(false);

  const renewalPrice = React.useMemo(() => {
    if (!state.lastSku || !pricing) return undefined;
    return findRenewalPrice(state.lastSku, state.isBrand, pricing);
  }, [state.lastSku, state.isBrand, pricing]);

  const renewMutation = useMutation({
    mutationFn: venueGetSubscriptionSessionApi,
    onSuccess: (sessionUrl) => {
      window.location.href = sessionUrl;
    },
    onError: () => {
      enqueueSnackbar('Something went wrong. Please try again later.', { variant: 'error' });
    },
  });

  const handleRenew = React.useCallback(() => {
    if (!renewalPrice) return;
    renewMutation.mutate(renewalPrice.id);
  }, [renewalPrice, renewMutation]);

  const handleChooseAnother = React.useCallback(() => {
    onClose();
    navigate('/plans');
  }, [navigate, onClose]);

  // Show a confirmation step before applying the persistent opt-out — this
  // is a destructive choice (less visibility / fewer promotions) and we want
  // it to be deliberate.
  const handleStayOnFreeClick = React.useCallback(() => {
    setShowStayFreeConfirm(true);
  }, []);

  const handleStayOnFreeConfirm = React.useCallback(() => {
    if (venueId != null) {
      localStorage.setItem(`${STAY_ON_FREE_STORAGE_PREFIX}${venueId}`, '1');
    }
    setShowStayFreeConfirm(false);
    onClose();
  }, [onClose, venueId]);

  const handleStayOnFreeCancel = React.useCallback(() => {
    setShowStayFreeConfirm(false);
  }, []);

  const handleContactSupport = React.useCallback(() => {
    onClose();
    chatContext?.setShouldChatOpen(true);
  }, [chatContext, onClose]);

  return (
    <>
      <BaseDialog
        open={open && !showStayFreeConfirm}
        onClose={onClose}
        showCloseIcon={false}
        disableEscapeKeyDown
        hideHeader
        sx={{
          '.MuiDialog-paper': {
            width: '702px',
            maxWidth: '702px',
            height: 'unset',
            padding: 0,
            borderRadius: '16px',
          },
        }}
      >
        <Box sx={{ px: 4, pt: 4, pb: 4 }}>
          <Stack spacing={7}>
            <Stack spacing={2} alignItems='flex-start'>
              {/* Eyebrow — pill outline in brand purple */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '999px',
                  border: '1px solid #765BC6',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: '#765BC6',
                    lineHeight: 1,
                  }}
                >
                  Continue your plan
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: '32px',
                  fontWeight: 600,
                  color: '#111',
                  lineHeight: 1.15,
                  letterSpacing: '-0.015em',
                }}
              >
                Action required to continue your plan
              </Typography>
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: '15px', color: '#111', lineHeight: 1.55 }}>
                  We&rsquo;ve upgraded our payment system.{' '}
                  <Box component='span' sx={{ fontWeight: 600 }}>
                    To keep your {state.isBrand ? 'account' : 'plan'} active, please confirm your
                    payment details.
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: '15px', color: '#111', lineHeight: 1.55 }}>
                  Your plan and pricing remain unchanged.
                </Typography>
              </Stack>
            </Stack>

            <Stack spacing={1.25} alignItems='stretch'>
              <Button
                variant='contained'
                fullWidth
                disableElevation
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                }}
                onClick={handleRenew}
                disabled={!renewalPrice || renewMutation.isPending}
              >
                {renewMutation.isPending ? 'Redirecting…' : 'Continue plan'}
              </Button>
              <Button
                variant='outlined'
                fullWidth
                disableElevation
                onClick={handleChooseAnother}
                disabled={renewMutation.isPending}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#111',
                  borderColor: '#E0E0E0',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderColor: '#111',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Change plan
              </Button>
              {/* Brands without a sub are effectively locked out of the
                  product, so we never let them "stay on Free". */}
              {!state.isBrand && (
                <Button
                  variant='text'
                  fullWidth
                  disableElevation
                  onClick={handleStayOnFreeClick}
                  disabled={renewMutation.isPending}
                  sx={{
                    borderRadius: '10px',
                    py: 1.5,
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#5C5C5C',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#111',
                    },
                  }}
                >
                  Stay on Free
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #F0F0F0',
            px: 4,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Button
            variant='text'
            startIcon={
              <SvgIcon name='Chat' sx={{ fontSize: '16px !important', color: '#111' }} />
            }
            onClick={handleContactSupport}
            disabled={renewMutation.isPending}
            sx={{
              minWidth: 0,
              p: 0,
              fontSize: '14px',
              fontWeight: 500,
              color: '#111',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '.MuiButton-startIcon': {
                marginRight: '6px',
              },
            }}
          >
            Contact support
          </Button>

          <Link
            component='button'
            type='button'
            onClick={() => setShowTerms(true)}
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: '#9A9A9A',
              textDecoration: 'underline',
              textDecorationColor: '#D0D0D0',
              textUnderlineOffset: '3px',
              '&:hover': {
                color: '#5C5C5C',
                textDecorationColor: '#9A9A9A',
              },
            }}
          >
            Terms &amp; Conditions
          </Link>
        </Box>
      </BaseDialog>

      <TermsAndConditionDialog
        showAgreementsButtons={false}
        termsData={termsData}
        open={showTerms}
        onClose={() => setShowTerms(false)}
      />

      <BaseDialog
        open={showStayFreeConfirm}
        onClose={handleStayOnFreeCancel}
        showCloseIcon={false}
        disableEscapeKeyDown
        hideHeader
        sx={{
          '.MuiDialog-paper': {
            width: '480px',
            maxWidth: '480px',
            height: 'unset',
            padding: 0,
            borderRadius: '16px',
          },
        }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 3 }}>
          <Stack spacing={4.5} alignItems='flex-start'>
            <Stack spacing={1} alignItems='flex-start'>
              <Typography
                sx={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#111',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                Are you sure?
              </Typography>
              <Typography sx={{ fontSize: '15px', color: '#111', lineHeight: 1.55 }}>
                If you stay on Free, your account will have less visibility and access to fewer
                promotions.
              </Typography>
            </Stack>
            <Stack direction='row' spacing={1.5} sx={{ width: '100%' }}>
              <Button
                variant='outlined'
                fullWidth
                disableElevation
                onClick={handleStayOnFreeCancel}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#111',
                  borderColor: '#E0E0E0',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderColor: '#111',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Go back
              </Button>
              <Button
                variant='contained'
                fullWidth
                disableElevation
                onClick={handleStayOnFreeConfirm}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Stay on Free
              </Button>
            </Stack>
          </Stack>
        </Box>
      </BaseDialog>
    </>
  );
};

export default MigrationRenewalDialog;
